import type {
  GeneratePlanDTO,
  GeneratedPlanSummary,
  GeneratedStageDTO,
  RouteSettings,
  StageType
} from '@my-roadtrip/types'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import { OpenAIService } from '../../services/openai.service.js'
import { ServiceUnavailableError, ValidationError } from '../../common/errors/index.js'
import { createLogger, trackPerformance } from '../../common/utils/logger.js'

interface RawStage {
  name?: string
  type?: string
  day?: number
  summary?: string
  location?: {
    lat?: number | string
    lng?: number | string
    address?: string
    placeId?: string
    name?: string
  }
  durationMinutes?: number | string
  arrivalTime?: string
  departureTime?: string
}

interface RawItineraryResponse {
  planName?: string
  description?: string
  tags?: string[]
  stages?: RawStage[]
  route?: {
    distanceKm?: number | string
    durationMinutes?: number | string
  }
  settings?: Partial<RouteSettings>
  metadata?: {
    model?: string
  }
}

export class PlannerAIService {
  private readonly openai: OpenAIService
  private readonly logger = createLogger('PlannerAIService')

  constructor(openaiService?: OpenAIService) {
    this.openai = openaiService ?? new OpenAIService()
  }

  /**
   * Generate plan with AI
   * 
   * Features:
   * - Performance tracking
   * - Fallback to baseline plan
   * - Structured error logging
   * 
   * @throws {ValidationError} If input is invalid
   * @throws {ServiceUnavailableError} If AI service fails and fallback fails
   */
  async generatePlan(
    input: GeneratePlanDTO,
    context?: { userId?: string }
  ): Promise<GeneratedPlanSummary> {
    // Input validation
    if (!input.origin || typeof input.origin !== 'string') {
      throw new ValidationError('Invalid origin', { origin: input.origin })
    }
    if (!Array.isArray(input.destinations) || input.destinations.length === 0) {
      throw new ValidationError('Invalid destinations', { destinations: input.destinations })
    }
    if (typeof input.days !== 'number' || input.days < 1 || input.days > 365) {
      throw new ValidationError('Invalid days (must be 1-365)', { days: input.days })
    }

    return await trackPerformance(
      'generatePlan',
      async () => {
        try {
          const raw = await this.openai.generateItinerary({
            origin: input.origin,
            destinations: input.destinations,
            days: input.days,
            travelers: input.travelers,
            travelMode: input.travelMode,
            pace: input.pace,
            themes: input.themes,
            language: input.language,
            preferences: input.preferences,
            budgetLevel: input.budgetLevel
          }, { userId: context?.userId, feature: 'planner:generate-plan' })

          const result = this.normalizeResponse(raw)
          this.logger.info('AI plan generated', {
            origin: input.origin,
            destinations: input.destinations.length,
            days: input.days,
            stagesGenerated: result.stages.length
          })
          return result
        } catch (error) {
          this.logger.warn('AI generation failed, falling back to baseline', {
            origin: input.origin,
            error: error instanceof Error ? error.message : 'Unknown error'
          })
          
          try {
            return this.buildBaselinePlan(input)
          } catch (fallbackError) {
            this.logger.error('Baseline plan generation failed', { error: fallbackError })
            throw new ServiceUnavailableError('AI service unavailable and fallback failed', {
              originalError: error,
              fallbackError
            })
          }
        }
      },
      this.logger,
      5000 // Warn if takes > 5s
    )
  }

  private normalizeResponse(raw: RawItineraryResponse): GeneratedPlanSummary {
    const stages = Array.isArray(raw.stages)
      ? raw.stages
          .map((stage, index, list) => this.normalizeStage(stage, index, list.length))
          .filter((stage): stage is GeneratedStageDTO => stage !== null)
      : []

    if (stages.length === 0) {
      throw new Error('AI returned no valid stages')
    }

    const uniqueTags = Array.isArray(raw.tags)
      ? Array.from(new Set(raw.tags.filter(tag => isNonEmptyString(tag))))
      : []

    const distance = this.toNumber(raw.route?.distanceKm)
    const duration = this.toNumber(raw.route?.durationMinutes)
    const route = (distance !== undefined && duration !== undefined)
      ? { distance, duration }
      : undefined

    const model = raw.metadata?.model || 'openai'

    return {
      name: raw.planName && raw.planName.trim().length > 0 ? raw.planName.trim() : stages[0].name,
      description: raw.description?.trim() || 'Itinéraire généré par IA',
      tags: uniqueTags,
      stages,
      route,
      settings: raw.settings,
      metadata: {
        model,
        strategy: 'ai'
      }
    }
  }

  private normalizeStage(stage: RawStage, index: number, total: number): GeneratedStageDTO | null {
    const name = isNonEmptyString(stage.name)
      ? stage.name.trim()
      : null
    if (!name) {
      return null
    }

    const location = stage.location ?? {}
    const lat = this.toNumber(location.lat)
    const lng = this.toNumber(location.lng)
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return null
    }

    const safeDay = typeof stage.day === 'number' && stage.day > 0
      ? stage.day
      : Math.floor(index / 2) + 1

    return {
      name,
      type: this.toStageType(stage.type, index, total),
      day: safeDay,
      summary: stage.summary?.trim(),
      location: {
        address: stage.location?.address ?? '',
        coordinates: { lat: lat ?? 0, lng: lng ?? 0 },
        placeId:
          typeof stage.location?.placeId === 'string' && stage.location.placeId.trim().length > 0
            ? stage.location.placeId.trim()
            : undefined,
        name:
          typeof stage.location?.name === 'string' && stage.location.name.trim().length > 0
            ? stage.location.name.trim()
            : undefined,
        lat: lat ?? 0,
        lng: lng ?? 0
      },
      durationMinutes: this.toNumber(stage.durationMinutes),
      arrivalTime: stage.arrivalTime,
      departureTime: stage.departureTime
    }
  }

  private toStageType(type: string | undefined, index: number, total: number): StageType {
    const normalized = typeof type === 'string' ? type.toLowerCase() : ''
    if (normalized === 'start' || normalized === 'waypoint' || normalized === 'end' || normalized === 'overnight' || normalized === 'poi') {
      return normalized as StageType
    }

    if (index === 0) return 'start'
    if (index === total - 1) return 'end'
    return 'waypoint'
  }

  private toNumber(value: number | string | undefined | null): number | undefined {
    if (typeof value === 'number') return value
    if (isString(value)) {
      const parsed = Number.parseFloat(value)
      return Number.isFinite(parsed) ? parsed : undefined
    }
    return undefined
  }

  private buildBaselinePlan(input: GeneratePlanDTO): GeneratedPlanSummary {
    const allStops = [input.origin, ...input.destinations]
    const stages: GeneratedStageDTO[] = allStops.map((label, index) => ({
      name: label,
      type: this.toStageType(undefined, index, allStops.length),
      day: Math.min(input.days, index + 1),
      summary: index === 0
        ? 'Point de départ recommandé. Ajustez selon vos préférences.'
        : 'Étape suggérée. Complétez avec vos idées favorites.',
      location: {
        address: label,
        coordinates: { lat: 0, lng: 0 }
      }
    }))

    return {
      name: `Road trip ${input.origin} → ${input.destinations.at(-1) ?? input.origin}`,
      description: 'Itinéraire de base généré sans IA (coordonnées à compléter).',
      tags: input.themes,
      stages,
      metadata: {
        model: 'baseline',
        strategy: 'baseline'
      }
    }
  }
}
